// Copyright (c) Docugami, Inc. All rights reserved.

import StatusBar from '../file_management/status-bar';
import styled from 'styled-components';
import { dgFontFamily } from 'components/common/Fonts';
import { modalShadow, primaryTheme } from 'components/shell/presentation/AppStyles';
import { ParentLimitsContainer } from 'components/common/LimitsContainer';
import { useAppSelector } from 'hooks/redux';
import { usePersistDivScrollPosition } from "hooks/persistScrollPosition";
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { useState, useLayoutEffect, useRef } from "react";
import { useAbstractPreview } from "hooks/useAbstractPreview";
import { selectIsReportPublished, selectIsReportSchemaLoaded, selectReportDocumentId, selectReportId } from 'store/reports/selectors';

// Required due to us using webpack 4 and CRA
// https://github.com/wojtekmaj/react-pdf#standard-browserify-esbuild-and-others
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const ViewContainer = styled.div`
    height: 100%;
    padding: 30px 10px 10px;
    box-sizing: border-box;
    min-width: 823px;
    overflow: auto;
    display: flex;
    justify-content: center;
`;

const ContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 823px;
    min-height: 90%;
    padding-top: 53px;
    height: fit-content;
    box-sizing: border-box;
    box-shadow: ${modalShadow};
    background: ${primaryTheme.palette.white};
    overflow: hidden;
    font-family: ${dgFontFamily};
`;

const ProgressContainer = styled.div`
    height: 10px;
    width: 100%;
`

export interface BaseAbstractViewProps {
    folderId: string;
    title: string;
}

export const AbstractView = (props: BaseAbstractViewProps): JSX.Element => {
    const isLoadingSchema = !useAppSelector(selectIsReportSchemaLoaded);
    const isPublished = !!useAppSelector(selectIsReportPublished);
    const reportId = useAppSelector(selectReportId);
    const folderId = useAppSelector(selectReportDocumentId);

    const currentDocument = useAppSelector(state => state.documents.indexed[props.folderId]?.clusterDocument);
    const scrollRef = useRef<HTMLDivElement | null>(null);

    const { isReady: isPdfReady, abstractPreviewUrl, isLoading } = useAbstractPreview({ folderId, reportId, isReportPublished: isPublished });
    const [numberOfPages, setNumberOfPages] = useState(0);

    useLayoutEffect(() => {
        scrollRef.current?.scrollTo({ top: 0, behavior: 'auto' });
    }, [props.folderId])
    usePersistDivScrollPosition(`abstractView`, scrollRef);

    function progressUx(): JSX.Element | null {
        return (
            <ProgressContainer>
                <StatusBar isInfinite={true} />
            </ProgressContainer>
        )
    }

    function evaluatedAbstractTable(): JSX.Element | null {
        if (isLoadingSchema || !currentDocument || isLoading) {
            return (
                <ContentContainer>
                    {progressUx()}
                </ContentContainer>
            );
        }

        const generatePages = (numberOfPages: number) => {

            const pages: JSX.Element[] = []
            for (let i = 0; i < numberOfPages; ++i) {
                pages.push(
                    <Page loading={''} pageIndex={i} width={823} key={`abstract_page_${i}`} />
                );
            }

            return pages;
        }

        return (
            <ContentContainer>
                {
                    isPdfReady ?
                        (
                            <Document
                                loading={''}
                                file={abstractPreviewUrl}
                                onLoadSuccess={({ numPages }) => {
                                    setNumberOfPages(numPages)
                                }}>
                                {
                                    generatePages(numberOfPages)
                                }
                            </Document>
                        )
                        : null
                }
            </ContentContainer>
        );
    }

    return (
        <ViewContainer ref={scrollRef}>
            <ParentLimitsContainer>
                {evaluatedAbstractTable()}
            </ParentLimitsContainer>
        </ViewContainer>
    );
}
